<script setup lang="ts" generic="T extends Record<string, any>">
    import { useVModel } from '@vueuse/core'
    import { useDialogConfirm } from 'composables'
    import PkFieldset from './PkFieldset.vue'
    import { watch } from 'vue'

    const props = withDefaults(
        defineProps<{
            modelValue: Partial<T>[] | undefined
            label?: string
            placeholder?: string
            defaultValue?: Partial<T> | (() => Partial<T>)
            move?: boolean
            showReset?: boolean
            invalid?: boolean
            invalidLabel?: string | string[]
            confirm?: boolean
            readonly?: boolean
            allowEmpty?: boolean
            force?: boolean
        }>(),
        {
            label: undefined,
            placeholder: undefined,
            defaultValue: () => ({}),
            invalidLabel: undefined,
            confirm: true,
            readonly: false,
            allowEmpty: false,
        },
    )
    const emit = defineEmits<{
        'update:modelValue': [value: T[]]
    }>()
    const localModelValue = useVModel(props, 'modelValue', emit)
    const addItem = () => {
        const newItem =
            typeof props.defaultValue === 'function'
                ? props.defaultValue()
                : (props.defaultValue ?? {})
        if (!localModelValue.value || !Array.isArray(localModelValue.value)) {
            // @ts-expect-error we want to reset the value
            localModelValue.value = [newItem]
            return
        }
        localModelValue.value.push(newItem)
        if (props.force) {
            // Force Vue to detect the change (in case defaultValue is an object and its reference doesn't change)
            localModelValue.value = JSON.parse(
                JSON.stringify(localModelValue.value),
            )
        }
    }
    watch(
        localModelValue,
        (newValue: Partial<T>[] | undefined) => {
            if (
                (newValue === undefined ||
                    newValue === null ||
                    newValue.length === 0) &&
                !props.allowEmpty
            ) {
                addItem()
            }
        },
        { immediate: true },
    )
    const { openDialogConfirm } = useDialogConfirm()
    const removeItem = async (index: number) => {
        if (props.confirm) {
            const result = await openDialogConfirm()
            if (!result) {
                return
            }
        }
        localModelValue.value.splice(index, 1)
    }
    const reset = async () => {
        if (props.confirm) {
            const result = await openDialogConfirm()
            if (!result) {
                return
            }
        }
        emit('update:modelValue', [])
    }
    const moveUp = (index: number) => {
        if (index === 0) {
            return
        }
        const item = localModelValue.value[index]
        localModelValue.value.splice(index, 1)
        localModelValue.value.splice(index - 1, 0, item)
    }
    const moveDown = (index: number) => {
        if (index === localModelValue.value.length - 1) {
            return
        }
        const item = localModelValue.value[index]
        localModelValue.value.splice(index, 1)
        localModelValue.value.splice(index + 1, 0, item)
    }
    defineExpose({
        addItem,
        removeItem,
        moveUp,
        moveDown,
    })
</script>

<template>
    <div
        class="pk-editor-repeater"
        :class="{
            'pk-editor-repeater--invalid': invalid,
            'pk-editor-repeater--empty': !localModelValue?.length,
        }">
        <PkFieldset :legend="label">
            <div class="pk-editor-repeater__wrapper">
                <ul class="pk-editor-repeater__list">
                    <li
                        v-for="(item, index) in localModelValue"
                        :key="index"
                        class="pk-editor-repeater__item">
                        <div class="pk-editor-repeater__item-wrapper">
                            <slot v-bind="{ index, item, readonly }" />
                        </div>
                        <div
                            v-if="!readonly"
                            class="pk-editor-repeater__item-actions">
                            <VvButtonGroup
                                modifiers="compact vertical"
                                item-modifiers="action-quiet">
                                <VvButton
                                    v-if="move"
                                    :title="$t('action.moveUp')"
                                    icon="ri:arrow-up-line"
                                    :disabled="index === 0"
                                    @click.stop="moveUp(index)" />
                                <VvButton
                                    :title="$t('action.remove')"
                                    icon="ri:delete-bin-line"
                                    @click.stop="removeItem(index)" />
                                <VvButton
                                    v-if="move"
                                    :title="$t('action.moveDown')"
                                    icon="ri:arrow-down-line"
                                    :disabled="
                                        index === localModelValue.length - 1
                                    "
                                    @click.stop="moveDown(index)" />
                            </VvButtonGroup>
                        </div>
                    </li>
                </ul>
                <div class="pk-editor-repeater__actions">
                    <div
                        v-if="invalid"
                        class="pk-editor-repeater__invalid-label">
                        {{
                            Array.isArray(invalidLabel)
                                ? invalidLabel.join(', ')
                                : invalidLabel
                        }}
                    </div>
                    <div
                        v-else-if="placeholder && localModelValue.length === 0"
                        class="pk-editor-repeater__placeholder">
                        {{ placeholder }}
                    </div>
                    <VvButtonGroup v-if="!readonly" class="mt-4">
                        <slot name="actions" />
                        <VvButton
                            v-if="showReset"
                            :label="$t('action.reset')"
                            icon="ri:delete-bin-line"
                            modifiers="action-quiet"
                            :disabled="localModelValue.length === 0"
                            @click.stop="reset" />
                        <VvButton
                            :label="$t('action.add')"
                            icon="ri:add-line"
                            :modifiers="
                                showReset ? 'action' : 'action-quiet-small'
                            "
                            :icon-position="showReset ? undefined : 'right'"
                            @click.stop="addItem" />
                    </VvButtonGroup>
                </div>
            </div>
        </PkFieldset>
    </div>
</template>

<style lang="scss">
    @layer volver.components {
        .pk-editor-repeater {
            padding-top: var(--spacing-8);
            margin-bottom: var(--spacing-md);

            .pk-fieldset {
                position: relative;
                padding: 0;
                margin: 0;

                legend {
                    position: absolute;
                    top: calc(-1 * var(--spacing-8));
                    left: var(--spacing-md);
                }
            }

            &__wrapper {
                overflow: hidden;
            }

            &__list {
                display: flex;
                flex-direction: column;
            }

            &__item {
                display: flex;
                border-bottom: var(--border) solid var(--color-surface-3);

                &-wrapper {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    flex-grow: 1;
                    padding-top: var(--spacing-md);
                    padding-inline: var(--spacing-md);
                }

                &:first-child {
                    .pk-editor-repeater__item-wrapper {
                        padding-top: var(--spacing-24);
                    }
                }

                &-actions {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    flex-shrink: 0;
                    padding: var(--spacing-6);
                    background-color: var(--color-surface-1);
                    border-left: var(--border) solid var(--color-surface-3);
                }
            }

            &__actions {
                min-height: var(--spacing-40);
                display: flex;
                justify-content: flex-end;
                padding: var(--input-spacing-top) var(--input-spacing-right)
                    var(--input-spacing-bottom) var(--input-spacing-left);
            }

            &__placeholder {
                color: var(--input-label-color);
                font-size: var(--input-hint-font-size);
                display: flex;
                align-items: center;
                margin-right: auto;
                padding: var(--spacing-12) var(--spacing-8) var(--spacing-8) 0;
            }

            &__invalid-label {
                color: var(--input-invalid-color);
                font-size: var(--input-hint-font-size);
                display: flex;
                align-items: center;
                margin-right: auto;
            }

            &--invalid {
                .pk-editor-repeater__wrapper {
                    border-color: var(--color-danger);
                }

                .pk-editor-repeater__legend {
                    color: var(--color-danger);
                }
            }
        }
    }
</style>
